import { API_ORIGIN, authorizedFetch, useAuth } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export type RecruiterClient = {
  id: string | null
  organisation_id: string
  organisation_name: string
  organisation_slug: string
  granted: boolean
  job_count: number
  recruiter_email?: string
}

export type RecruiterClientJob = {
  id: string
  title: string
  publication_status: string
  organisation_name: string
  organisation_slug: string
}

async function readError(response: Response, fallback: string) {
  const body = await response.json().catch(() => ({}))
  return body.error || body.detail || fallback
}

export function useRecruiterClients() {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['recruiter-clients'],
    enabled: Boolean(token),
    queryFn: async () => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/applications/recruiter-clients/`)
      if (!response.ok) throw new Error(await readError(response, 'Failed to load clients'))
      return (await response.json()) as RecruiterClient[]
    },
  })
}

export function useSelectRecruiterClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (organisationId: string) => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/applications/recruiter-clients/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organisation_id: organisationId }),
      })
      if (!response.ok) throw new Error(await readError(response, 'Failed to select client'))
      return (await response.json()) as RecruiterClient
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recruiter-clients'] })
    },
  })
}

export function useRecruiterClientJobs(organisationSlug: string | null) {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['recruiter-client-jobs', organisationSlug],
    enabled: Boolean(token && organisationSlug),
    queryFn: async () => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/recruiter-clients/organisations/${encodeURIComponent(organisationSlug!)}/jobs/`,
      )
      if (!response.ok) throw new Error(await readError(response, 'Failed to load jobs'))
      return (await response.json()) as RecruiterClientJob[]
    },
  })
}

export function useRecruiterGrants() {
  const { token } = useAuth()
  return useQuery({
    queryKey: ['recruiter-client-grants'],
    enabled: Boolean(token),
    queryFn: async () => {
      const response = await authorizedFetch(`${API_ORIGIN}/api/applications/recruiter-clients/grants/`)
      if (!response.ok) throw new Error(await readError(response, 'Failed to load recruiter access'))
      return (await response.json()) as RecruiterClient[]
    },
  })
}

export function useGrantRecruiterClient() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (linkId: string) => {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/applications/recruiter-clients/${encodeURIComponent(linkId)}/grant/`,
        { method: 'POST' },
      )
      if (!response.ok) throw new Error(await readError(response, 'Failed to grant access'))
      return (await response.json()) as RecruiterClient
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['recruiter-client-grants'] })
    },
  })
}
